import { useExtractDocs } from "context/ExtractDocsContext";
import {
  FILE_SHARE_MODE_NONE,
  FILE_TABLE_ACTION_SHARE,
  QUERY_PROGRESS_PENDING,
  TRACK_EVENTS,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { isFileHISStateful } from "core/model/files/utils";
import {
  FileState,
  FileTableActions,
  Filev2Stateful,
  QueryProgress,
} from "core/types";
import GeneralTable from "ds_legacy/components/Tables/GeneralTable";
import {
  COLUMN_TYPE_DEFAULT_TOOLTIP,
  COLUMN_TYPE_FILES_ACTIONS,
  COLUMN_TYPE_FILES_CATEGORY,
  COLUMN_TYPE_FILES_DATE_AND_SOURCE,
  COLUMN_TYPE_FILES_FILE_NAME,
  COLUMN_TYPE_FILES_SHARED_WITH,
} from "ds_legacy/components/Tables/GeneralTable/CustomCells";
import { EmptyV2 } from "ds_legacy/components/Tables/GeneralTable/EmptyComponent";
import { SetAction } from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import {
  getAllowList,
  getFileListActionOptions,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions/Files";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import {
  getFileNameAndIsNewVersionAvailable,
  isFileForm,
  isFileFormUploaded,
} from "dsl/ecosystems/PatientFiles/utils";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import { FilesTableTitle } from "dsl/organisms/FileTables";
import { useMemo } from "react";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";

/**
 * Column width percentages for AvailableFiles — data columns + overflow menu (`GeneralTable` `actions.width`, 4%) = 100%.
 * Without reserving the menu column, `table-layout: fixed` collapses the kebab cell and actions overlap it.
 */
const AVAILABLE_FILES_WIDTHS = {
  /** Consultation + extract: category + fileName + date + employee + file actions + menu = 100% (sharedWith hidden). */
  consultationExtract: { fileName: "34%" },
  /** Consultation, extract off: category + fileName + date + employee + menu (sharedWith + file actions hidden). */
  consultationPlain: {
    category: "10%",
    dateSource: "18%",
    employee: "16%",
    fileName: "52%",
  },
  /** Default patient files, extract off: five data columns + row actions menu (no extract actions column). */
  default: {
    category: "9%",
    dateSource: "13%",
    employee: "11%",
    fileName: "28%",
    sharedWith: "35%",
  },
  extract: {
    actions: "26%",
    category: "10%",
    dateSource: "14%",
    employee: "12%",
    fileName: "20%",
    sharedWith: "12%",
  },
  /** Reserved for `GeneralTable` `actions.width` (kebab column); kept in sync with column math above. */
  overflowMenu: "4%",
} as const;

type AvailableFilesColumnWidthKey =
  | "category"
  | "dateSource"
  | "employee"
  | "fileName";

function getAvailableFilesColumnWidth(
  column: AvailableFilesColumnWidthKey,
  isConsultation: boolean | undefined,
  isExtractActive: boolean,
): string {
  if (isConsultation) {
    if (column === "category") {
      return AVAILABLE_FILES_WIDTHS.consultationPlain.category;
    }

    if (column === "fileName") {
      return isExtractActive
        ? AVAILABLE_FILES_WIDTHS.consultationExtract.fileName
        : AVAILABLE_FILES_WIDTHS.consultationPlain.fileName;
    }

    if (isExtractActive) {
      return AVAILABLE_FILES_WIDTHS.extract[column];
    }

    return AVAILABLE_FILES_WIDTHS.consultationPlain[column];
  }

  if (isExtractActive) {
    return AVAILABLE_FILES_WIDTHS.extract[column];
  }

  return AVAILABLE_FILES_WIDTHS.default[column];
}

function getFileSourceLabel(
  file: FileState,
  fileListComponent: {
    sourceComputerInfo: string;
    sourceHISInfo: string;
    sourceRecareInfo: string;
  },
): string {
  if (isFileHISStateful(file) || file.kis_document_id_string) {
    return fileListComponent.sourceHISInfo;
  }

  if (isFileFormUploaded(file)) {
    return fileListComponent.sourceRecareInfo;
  }

  return fileListComponent.sourceComputerInfo;
}

export type AvailableFilesProps = {
  careseekerId: number | null;
  files?: FileState[] | null;
  onOpenDetails: (file: FileState) => void;
  queryProgress: QueryProgress;
  setAction: SetAction<FileFeatureData>;
  titleButton?: React.JSX.Element;
  isConsultation?: boolean;
};

export function AvailableFiles({
  careseekerId,
  files,
  isConsultation,
  onOpenDetails,
  queryProgress,
  setAction,
  titleButton,
}: AvailableFilesProps) {
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const { extractOcrStatus, utils } = useExtractDocs();

  const extractionStatusKey = useMemo(
    () =>
      extractOcrStatus.result?.documents
        .map((doc) => `${doc.recare_file_id || doc.kis_file_id}:${doc.status}`)
        .sort()
        .join(",") ?? "",
    [extractOcrStatus.result],
  );

  const isExtractActive = isDocsAndExtractActive || isDischargeLetterActive;

  return (
    <VerticalLayout>
      <FilesTableTitle
        button={titleButton}
        description={
          translations.fileSection.fileListComponent.descriptionFilesAvailable
        }
        numberOfFiles={files?.length ?? 0}
        title={translations.fileSection.fileListComponent.titleFilesAvailable}
      />
      <GeneralTable<FileState, FileTableActions>
        actions={{
          allowList: () =>
            getAllowList({
              fileTable: "available",
              isDocsAndExtractActive:
                isDocsAndExtractActive || isDischargeLetterActive,
            }),
          getOptions: ({ data: file, translations }) =>
            getFileListActionOptions({
              canAccessPatientData: true,
              file,
              fileTable: "available",
              isExtractActive,
              isFileDeleted: isFileHISStateful(file)
                ? false
                : (file as Filev2Stateful).isFileDeleted,
              isFileExtracted: utils.isFileExtracted(file),
              isNewVersionAvailable: isFileHISStateful(file)
                ? false
                : (file as Filev2Stateful).isNewVersionAvailable,
              onOpenDetails,
              setAction,
              trackEvent,
              translations,
            }),
          getTestId: (file) => `action-available-file-${file.id}`,
          onActionMenuClick: () => {
            trackEvent({
              file_implementation: "HIS",
              name: TRACK_EVENTS.FILE_MENU_CLICKED,
              section: "available",
            });
          },
          tour: () =>
            tourAttributes({
              stepKey:
                PRODUCT_TOURS.available_file_to_share.steps
                  .file_table_action_menu.key,
              tourKey: PRODUCT_TOURS.available_file_to_share.key,
            }),
          width: AVAILABLE_FILES_WIDTHS.overflowMenu,
        }}
        columns={[
          {
            getProps: (data) => ({
              category: isFileHISStateful(data)
                ? data.document_category_number
                : data.category,
            }),
            title: translations.fileSection.fileListComponent.category,
            type: COLUMN_TYPE_FILES_CATEGORY,
            width: getAvailableFilesColumnWidth(
              "category",
              isConsultation,
              isExtractActive,
            ),
          },
          {
            getProps: (file) =>
              getFileNameAndIsNewVersionAvailable({
                canAccessPatientData: true,
                file,
              }),
            title: translations.fileSection.fileListComponent.fileName,
            type: COLUMN_TYPE_FILES_FILE_NAME,
            width: getAvailableFilesColumnWidth(
              "fileName",
              isConsultation,
              isExtractActive,
            ),
          },
          {
            getProps: (file) => ({
              source: getFileSourceLabel(
                file,
                translations.fileSection.fileListComponent,
              ),
              uploadDate: isFileHISStateful(file)
                ? // creation_date is the date the file was created in the HIS,
                  // updated_at is when the gateway got the file (fallback)
                  file.creation_date ?? file.updated_at
                : file.created_at,
            }),
            title: translations.fileSection.fileListComponent.dateSourceColumn,
            type: COLUMN_TYPE_FILES_DATE_AND_SOURCE,
            width: getAvailableFilesColumnWidth(
              "dateSource",
              isConsultation,
              isExtractActive,
            ),
          },
          {
            getProps: (file) => {
              return {
                value:
                  isFileHISStateful(file) || isFileForm(file)
                    ? translations.fileSection.fileListComponent.sourceHIS
                    : `${file.account?.first_name ?? ""} ${
                        file.account?.last_name ?? ""
                      }`.trim(),
              };
            },
            title: translations.fileSection.fileListComponent.employeeColumn,
            type: COLUMN_TYPE_DEFAULT_TOOLTIP,
            width: getAvailableFilesColumnWidth(
              "employee",
              isConsultation,
              isExtractActive,
            ),
          },
          {
            getProps: (file) =>
              isFileHISStateful(file)
                ? {
                    careseekerId,
                    share_mode: FILE_SHARE_MODE_NONE,
                    shared_with: undefined,
                  }
                : {
                    careseekerId,
                    share_mode: file.share_mode,
                    shared_with: file.shared_with,
                  },
            isHidden: isConsultation,
            title: translations.fileSection.fileListComponent.sharedWith,
            type: COLUMN_TYPE_FILES_SHARED_WITH,
            width: isExtractActive
              ? AVAILABLE_FILES_WIDTHS.extract.sharedWith
              : AVAILABLE_FILES_WIDTHS.default.sharedWith,
          },
          {
            getProps: (file: FileState) => {
              return {
                file,
                isConsultation,
                onShareClick: () => {
                  trackEvent({
                    file_implementation: "HIS",
                    name: TRACK_EVENTS.FILE_MENU_MAIN_SHARE_BUTTON_CLICKED,
                    section: "available",
                  });
                  setAction({ data: file, type: FILE_TABLE_ACTION_SHARE });
                },
              };
            },
            isHidden: !isExtractActive,
            title: translations.extractDocs.filesTabChanges.columnTitleActions,
            type: COLUMN_TYPE_FILES_ACTIONS,
            width: isExtractActive
              ? AVAILABLE_FILES_WIDTHS.extract.actions
              : undefined,
          },
        ]}
        data={files ?? []}
        dependencies={[extractionStatusKey]}
        emptyComponent={
          <EmptyV2
            title={
              translations.fileSection.fileListComponent
                .placeholderNothingUploaded
            }
          />
        }
        getRowTestId={(file) =>
          isFileHISStateful(file)
            ? `available-files-his-row-${file.id}`
            : `available-files-row-${file.id}`
        }
        id="available-files"
        isLoading={queryProgress === QUERY_PROGRESS_PENDING}
      />
    </VerticalLayout>
  );
}
export default AvailableFiles;
